import { z } from 'zod';
import { str, nstr } from './schemaPrimitives';

/* ---------- Access: API tokens and CI trust (OIDC) ---------- */
/*
 * `ApiToken` never carries the token itself: the hash stays in the API and the plaintext exists only in the create
 * response (`ApiTokenCreated.token`), which the create dialog shows once and drops. Scopes stay strings so a scope the
 * console does not know yet still renders.
 */
export const ApiToken = z.object({
  id: str,
  name: str,
  /** `personal` (bound to its owner's access) or `service` (org-owned, admin); `oidc` rows are never listed. */
  kind: str,
  userId: nstr,
  tokenPrefix: str,
  scopes: z.array(str).default([]),
  /** `null` = every project of the org. */
  projectIds: z.array(str).nullish(),
  expiresAt: nstr,
  lastUsedAt: nstr,
  lastUsedIp: nstr,
  createdBy: nstr,
  createdAt: str,
  revokedAt: nstr,
});
export type ApiToken = z.infer<typeof ApiToken>;
export const ApiTokenList = z.object({ data: z.array(ApiToken), nextCursor: z.string().nullable() });
export const ApiTokenCreated = ApiToken.extend({ token: str });
export type ApiTokenCreated = z.infer<typeof ApiTokenCreated>;

export const OidcPolicy = z.object({
  id: str,
  projectId: nstr,
  name: str,
  scm: str,
  issuer: str,
  audience: str,
  /** Claim → exact value, or a list of globs (any of them); every condition must match. */
  claimConditions: z.record(str, z.union([str, z.array(str)])).default({}),
  scopes: z.array(str).default([]),
  /** `null` = only the project's non-protected environments. */
  environmentIds: z.array(str).nullish(),
  /** Slugs of `environmentIds`. */
  environments: z.array(str).default([]),
  maxTtlSeconds: z.number(),
  disabled: z.boolean().default(false),
  disabledAt: nstr,
  createdBy: nstr,
  createdAt: str,
  etag: str.optional(),
});
export type OidcPolicy = z.infer<typeof OidcPolicy>;
export const OidcPolicyList = z.object({ data: z.array(OidcPolicy), nextCursor: z.string().nullable() });
